/**
 * @fileoverview RouterHelper class for secure and efficient module loading operations.
 * 
 * This file contains the RouterHelper singleton that provides centralized module loading
 * functionality with security features, race condition prevention, timeout protection,
 * and comprehensive logging. It serves as the core utility for all module loaders.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

/**
 * Metadata interface for tracking individual module loading state.
 * Contains comprehensive information about each module's loading lifecycle.
 */
interface ModuleMetadata {
    /** Unique identifier for the module */
    readonly key: string;
    
    /** Timestamp when the module was loaded (milliseconds since epoch) */
    readonly loadedAt: number;
    
    /** Webpack chunk name for the module (used in logging and debugging) */
    readonly chunkName: string;
    
    /** Current loading status of the module */
    readonly status: 'loading' | 'loaded' | 'failed';
}

/**
 * RouterHelper provides secure, efficient module loading with advanced features.
 * 
 * This singleton class offers:
 * - Race condition prevention through promise management
 * - Timeout protection for hanging modules (30s default)
 * - Security features: input validation, XSS prevention, sanitized logging
 * - Comprehensive module metadata tracking
 * - Duplicate loading prevention
 * - Enhanced error handling and logging
 * 
 * Security Features:
 * - Input sanitization for module keys and chunk names
 * - Console injection prevention
 * - XSS protection in logging messages
 * - Module key format validation (alphanumeric, hyphens, underscores only)
 * 
 * Performance Optimizations:
 * - Efficient module tracking with Map data structures
 * - Promise reuse for concurrent module requests
 * - Race condition prevention
 * - Memory-efficient metadata storage
 * 
 * @class RouterHelper
 */
export class RouterHelper {
    /** Singleton instance of the RouterHelper */
    private static instance: RouterHelper;
    
    /** Map storing metadata for all loaded modules, keyed by module identifier */
    private readonly loadedModules: Map<string, ModuleMetadata> = new Map();
    
    /** Map storing active loading promises to prevent duplicate loading attempts */
    private readonly loadingPromises: Map<string, Promise<void>> = new Map();

    private showConsole:boolean = true;
    
    /**
     * Gets the singleton instance of RouterHelper.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton RouterHelper instance
     */
    static getInstance(): RouterHelper {
        this.instance ??= new RouterHelper();
        return this.instance;
    }
    
    /**
     * Private constructor to enforce singleton pattern.
     * Prevents direct instantiation of RouterHelper.
     */
    private constructor() {
        // Private constructor for singleton pattern
    }

    /**
     * Checks if a module is already loaded and available.
     * 
     * @param moduleKey - The unique identifier for the module
     * @returns true if module is successfully loaded, false otherwise
     */
    isModuleLoaded(moduleKey: string): boolean {
        const metadata = this.loadedModules.get(moduleKey);
        return metadata?.status === 'loaded';
    }

    /**
     * Checks if a module is currently loading
     * @param moduleKey - The key identifier for the module
     * @returns true if module is currently loading, false otherwise
     */
    isModuleLoading(moduleKey: string): boolean {
        const metadata = this.loadedModules.get(moduleKey);
        return metadata?.status === 'loading';
    }

    /**
     * Gets the loading promise for a module if it's currently loading
     * @param moduleKey - The key identifier for the module
     * @returns Promise if module is loading, undefined otherwise
     */
    getLoadingPromise(moduleKey: string): Promise<void> | undefined {
        return this.loadingPromises.get(moduleKey);
    }

    /**
     * Marks a module as loading
     * @param moduleKey - The key identifier for the module
     * @param chunkName - The webpack chunk name
     * @param promise - The loading promise
     */
    markModuleAsLoading(moduleKey: string, chunkName: string, promise: Promise<void>): void {
        if (!this.isValidModuleKey(moduleKey)) {
            throw new Error(`Invalid module key: ${moduleKey}`);
        }
        
        this.loadedModules.set(moduleKey, {
            key: moduleKey,
            loadedAt: Date.now(),
            chunkName: this.sanitizeChunkName(chunkName),
            status: 'loading'
        });
        
        this.loadingPromises.set(moduleKey, promise);
    }

    /**
     * Marks a module as loaded
     * @param moduleKey - The key identifier for the module
     */
    markModuleAsLoaded(moduleKey: string): void {
        const metadata = this.loadedModules.get(moduleKey);
        if (metadata) {
            this.loadedModules.set(moduleKey, {
                ...metadata,
                status: 'loaded',
                loadedAt: Date.now()
            });
        }
        this.loadingPromises.delete(moduleKey);
    }

    /**
     * Marks a module as failed and cleans up loading state.
     * 
     * Updates the module's status to 'failed' and removes any active loading
     * promises to prevent memory leaks and allow retry attempts. Also triggers
     * periodic cleanup of old failed modules to maintain optimal memory usage.
     * 
     * @param moduleKey - The unique identifier for the module
     */
    markModuleAsFailed(moduleKey: string): void {
        const metadata = this.loadedModules.get(moduleKey);
        if (metadata) {
            this.loadedModules.set(moduleKey, {
                ...metadata,
                status: 'failed'
            });
        }
        this.loadingPromises.delete(moduleKey);
        
        // Trigger periodic cleanup of old failed modules (every 10 failures)
        const failedCount = Array.from(this.loadedModules.values())
            .filter(m => m.status === 'failed').length;
        
        if (failedCount > 0 && failedCount % 10 === 0) {
            // Run cleanup asynchronously to avoid blocking the current operation
            setTimeout(() => this.cleanupFailedModules(), 0);
        }
    }

    /**
     * Retrieves comprehensive metadata for a specific module.
     * 
     * Returns detailed information about the module's loading state,
     * including status, timestamps, and chunk information.
     * 
     * @param moduleKey - The unique identifier for the module
     * @returns Module metadata object or undefined if module not found
     */
    getModuleMetadata(moduleKey: string): ModuleMetadata | undefined {
        return this.loadedModules.get(moduleKey);
    }

    /**
     * Gets all loaded modules
     * @returns Array of module metadata
     */
    getAllLoadedModules(): ModuleMetadata[] {
        return Array.from(this.loadedModules.values()).filter(m => m.status === 'loaded');
    }

    /**
     * Validates module key format
     * @param moduleKey - The key to validate
     * @returns true if valid, false otherwise
     */
    private isValidModuleKey(moduleKey: string): boolean {
        // Only allow alphanumeric characters, hyphens, and underscores
        return /^[a-zA-Z0-9_-]+$/.test(moduleKey) && moduleKey.length > 0 && moduleKey.length <= 50;
    }

    /**
     * Sanitizes chunk name to prevent XSS
     * @param chunkName - The chunk name to sanitize
     * @returns Sanitized chunk name
     */
    private sanitizeChunkName(chunkName: string): string {
        return chunkName.replace(/[<>'"&]/g, '');
    }

   
    /**
     * Determines if the current environment is a test/development environment.
     * 
     * Checks the hostname against known test environments (sandbox, dev, test, localhost).
     * Uses a performance-optimized caching strategy:
     * - In production: Checks once, then never re-evaluates (optimal performance)
     * - In test/dev: Re-checks on each call to ensure accuracy (negligible overhead)
     * 
     * The caching mechanism works by:
     * 1. Starting with showConsole = true (unchecked state)
     * 2. First call checks hostname and sets showConsole to true/false
     * 3. Production (false): Skips check on all subsequent calls
     * 4. Test/Dev (true): Re-validates on subsequent calls for safety
     * 
     * @returns true if running in test/dev environment (nblysbx, nblydev, nblytest, localhost), false for production
     * 
     * @example
     * // In production (www.example.com)
     * isTestEnvironment(); // First call: checks hostname → returns false
     * isTestEnvironment(); // Subsequent calls: skips check → returns false (cached)
     * 
     * @example
     * // In test environment (nblytest.example.com)
     * isTestEnvironment(); // First call: checks hostname → returns true
     * isTestEnvironment(); // Subsequent calls: re-checks hostname → returns true
     */
    isTestEnvironment(): boolean {
        if (this.showConsole){
            const host = window?.location?.hostname?.toLowerCase() ?? '';
            const testHosts = ['nblysbx', 'nblydev', 'nblytest', 'localhost'];
        
            this.showConsole =  testHosts.some(testHost => host.includes(testHost));
        }
        return this.showConsole;
    }

     /**
     * Enhanced console logging with module context
     * @param message - The message to log
     * @param isError - Whether this is an error message
     * @param moduleKey - Optional module key for context
     */
    routerConsoleLog(message: string, isError: boolean = false, moduleKey?: string): void {
        if(!this.isTestEnvironment()) return;
        
        if (typeof message !== 'string') {
            message = String(message);
        }
        
        // Sanitize message to prevent console injection
        const sanitizedMessage = message.replace(/[<>'"&]/g, '');
        
        const dynamicColor = isError ? '#c0392b' : '#ecf0f1';
        const fontSize = isError ? '8px' : '10px';
        const moduleContext = moduleKey ? ` [${moduleKey}]` : '';
        
        const logMethod = isError ? console.error : console.log;
        logMethod(
            `%c[Router]${moduleContext}%c ${sanitizedMessage}`, 
            `background:#8e44ad; color: ${dynamicColor}; font-weight: bold; padding: 0 8px; font-size: ${fontSize}`, 
            ''
        );
    }

    /**
     * Cleans up failed modules to prevent memory leaks.
     * 
     * Removes failed module entries from the loadedModules Map to prevent
     * indefinite memory accumulation. This method should be called periodically
     * or after a certain number of failures to maintain optimal memory usage.
     * 
     * @param maxAge - Optional maximum age in milliseconds for failed modules to keep (default: 5 minutes)
     * @returns Number of failed modules that were cleaned up
     */
    cleanupFailedModules(maxAge: number = 5 * 60 * 1000): number {
        const currentTime = Date.now();
        let cleanedCount = 0;
        
        for (const [moduleKey, metadata] of this.loadedModules.entries()) {
            if (metadata.status === 'failed') {
                const failureAge = currentTime - metadata.loadedAt;
                
                if (failureAge > maxAge) {
                    this.loadedModules.delete(moduleKey);
                    cleanedCount++;
                    this.routerConsoleLog(
                        `Cleaned up failed module: ${moduleKey} (age: ${Math.round(failureAge / 1000)}s)`,
                        false,
                        'RouterHelper'
                    );
                }
            }
        }
        
        if (cleanedCount > 0) {
            this.routerConsoleLog(
                `Cleanup completed: ${cleanedCount} failed modules removed`,
                false,
                'RouterHelper'
            );
        }
        
        return cleanedCount;
    }

    /**
     * Clears all modules and loading promises.
     * 
     * Completely resets the RouterHelper state by clearing all tracked modules
     * and active loading promises. Use with caution as this will lose all
     * module loading history and state.
     */
    clearAllModules(): void {
        this.loadedModules.clear();
        this.loadingPromises.clear();
    }

    /**
     * Gets comprehensive statistics about failed modules.
     * 
     * Provides detailed information about failed modules including count,
     * age distribution, and cleanup recommendations for memory management.
     * 
     * @returns Object containing failed module statistics and recommendations
     */
    getFailedModuleStats(): {
        count: number;
        oldestFailureAge: number;
        averageAge: number;
        recommendCleanup: boolean;
        modules: Array<{ key: string; age: number; chunkName: string }>;
    } {
        const currentTime = Date.now();
        const failedModules = Array.from(this.loadedModules.entries())
            .filter(([, metadata]) => metadata.status === 'failed')
            .map(([key, metadata]) => ({
                key,
                age: currentTime - metadata.loadedAt,
                chunkName: metadata.chunkName
            }));

        const ages = failedModules.map(m => m.age);
        const oldestFailureAge = ages.length > 0 ? Math.max(...ages) : 0;
        const averageAge = ages.length > 0 ? ages.reduce((sum, age) => sum + age, 0) / ages.length : 0;
        
        return {
            count: failedModules.length,
            oldestFailureAge,
            averageAge,
            recommendCleanup: failedModules.length > 5 || oldestFailureAge > 10 * 60 * 1000, // 10 minutes
            modules: failedModules.sort((a, b) => b.age - a.age) // Sort by age, oldest first
        };
    }

    getModuleStatus(): { loaded: any[], total: number, loadedCount: number } {
        const loadedModules = this.getAllLoadedModules();
        return {
            loaded: loadedModules,
            total: loadedModules.length,
            loadedCount: loadedModules.filter(m => m.status === 'loaded').length
        };
    }

    
    /**
     * Module loading with race condition prevention and timeout
     * @param moduleKey - The key identifier for the module
     * @param importFunction - Function that returns the import promise
     * @param chunkName - The webpack chunk name for logging
     * @param timeoutMs - Timeout in milliseconds (default: 30000ms = 30 seconds)
     * @returns Promise that resolves when module is loaded
     */
    async loadModuleSafely(
        moduleKey: string, 
        importFunction: () => Promise<any>, 
        chunkName: string,
        timeoutMs: number = 30000
    ): Promise<void> {
        // Check if already loaded
        if (this.isModuleLoaded(moduleKey)) {
            this.routerConsoleLog(`${chunkName} already loaded`, false, moduleKey);
            return;
        }

        // Check if currently loading - wait for existing promise
        if (this.isModuleLoading(moduleKey)) {
            const existingPromise = this.getLoadingPromise(moduleKey);
            if (existingPromise) {
                this.routerConsoleLog(`${chunkName} already loading, waiting...`, false, moduleKey);
                return existingPromise;
            }
        }

        // Start loading with timeout
        const loadingPromise = this.performModuleLoadWithTimeout(moduleKey, importFunction, chunkName, timeoutMs);
        this.markModuleAsLoading(moduleKey, chunkName, loadingPromise);
        
        return loadingPromise;
    }

    /**
     * Performs the actual module loading with timeout protection
     * @param moduleKey - The key identifier for the module
     * @param importFunction - Function that returns the import promise
     * @param chunkName - The webpack chunk name for logging
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise that resolves when module is loaded
     */
    async performModuleLoadWithTimeout(
        moduleKey: string, 
        importFunction: () => Promise<any>, 
        chunkName: string,
        timeoutMs: number
    ): Promise<void> {
        try {
            // Create timeout promise
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new Error(`Module loading timeout after ${timeoutMs}ms`));
                }, timeoutMs);
            });

            // Race between import and timeout
            await Promise.race([
                importFunction(),
                timeoutPromise
            ]);
            
            this.markModuleAsLoaded(moduleKey);
            this.routerConsoleLog(`${chunkName} loaded successfully`, false, moduleKey);
        } catch (error) {
            this.markModuleAsFailed(moduleKey);
            const errorMessage = error instanceof Error ? error.message : String(error);
            
            // Check if it's a timeout error
            if (errorMessage.includes('timeout')) {
                this.routerConsoleLog(`${chunkName} timed out after ${timeoutMs}ms`, true, moduleKey);
            } else {
                this.routerConsoleLog(`${chunkName} failed to load - ${errorMessage}`, true, moduleKey);
            }
            
            throw error; // Re-throw to allow caller to handle
        }
    }

    /**
     * Legacy method for backward compatibility
     * @deprecated Use performModuleLoadWithTimeout instead
     */
    async performModuleLoad(
        moduleKey: string, 
        importFunction: () => Promise<any>, 
        chunkName: string
    ): Promise<void> {
        return this.performModuleLoadWithTimeout(moduleKey, importFunction, chunkName, 30000);
    }
}
